import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/backoffice/components/layout';
import { Card, Button, Input } from '@/backoffice/components/ui';
import { fetchRelayPointStaffActivities, logRelayPointStaffActivity } from '@/domains/api/relay-point.api';
import { formatRelativeTime } from '@/backoffice/utils';
import { Loader2, Plus } from 'lucide-react';

const EVENT_TYPES = [
  { value: 'deposit', label: 'Dépôt colis' },
  { value: 'pickup', label: 'Retrait client' },
  { value: 'handoff', label: 'Remise livreur' },
  { value: 'scan', label: 'Scan contrôle' },
];

export function RelayPointActivitiesPage() {
  const [activities, setActivities] = useState<Array<Record<string, unknown>>>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({ event_type: 'deposit', notes: '', order_request_id: '' });

  const load = () => {
    setLoading(true);
    fetchRelayPointStaffActivities()
      .then(setActivities)
      .catch(() => setActivities([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async () => {
    setSaving(true);
    try {
      await logRelayPointStaffActivity({
        event_type: form.event_type,
        notes: form.notes || null,
        order_request_id: form.order_request_id ? Number(form.order_request_id) : null,
      });
      setForm({ event_type: 'deposit', notes: '', order_request_id: '' });
      load();
    } finally {
      setSaving(false);
    }
  };

  return (
    <DashboardLayout userType="relay_point" title="Activités">
      <Card className="mb-6">
        <h3 className="font-semibold mb-4">Enregistrer une activité</h3>
        <div className="grid md:grid-cols-3 gap-3 mb-3">
          <select
            className="border rounded-lg px-3 py-2 text-sm"
            value={form.event_type}
            onChange={(e) => setForm((f) => ({ ...f, event_type: e.target.value }))}
          >
            {EVENT_TYPES.map((t) => (
              <option key={t.value} value={t.value}>{t.label}</option>
            ))}
          </select>
          <Input
            placeholder="N° commande (optionnel)"
            value={form.order_request_id}
            onChange={(e) => setForm((f) => ({ ...f, order_request_id: e.target.value }))}
          />
          <Input
            placeholder="Notes"
            value={form.notes}
            onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
          />
        </div>
        <Button leftIcon={<Plus className="w-4 h-4" />} isLoading={saving} onClick={handleSubmit}>
          Enregistrer
        </Button>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><Loader2 className="w-8 h-8 animate-spin text-teal-600" /></div>
      ) : (
        <div className="space-y-2">
          {activities.map((ev) => (
            <Card key={String(ev.id)}>
              <div className="flex justify-between text-sm">
                <div>
                  <p className="font-medium">{String(ev.event_type)}</p>
                  {ev.notes && <p className="text-gray-600">{String(ev.notes)}</p>}
                  {ev.order_code && <p className="text-xs text-gray-400">Commande {String(ev.order_code)}</p>}
                </div>
                <p className="text-xs text-gray-400">
                  {ev.created_at ? formatRelativeTime(String(ev.created_at)) : ''}
                </p>
              </div>
            </Card>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}
